/**
 * Derives J-code document rows and selected medicines for the NAR letter modal.
 * @module useDocMetaData
 */

import { useEffect, useState } from "react";
import { DocumentsStoreedInLocalStorage } from "../../../../data-model/docsStorageInLocalStorage";
import { AuthOnFile } from "../../../../data-model/medicalPaOrdersModel";
import { UserGeneralInfoContextKeys } from "../../../../enums/userGeneralInfoContextKeys";
import { UserStore } from "../../../../store/UserGeneralInfoStore";
import { getJCodesWithAuthLetterDocPathFromAuthOnFile } from "../../../../utils/getJCodesWithAuthLetterDocPath";
import { NarLetterModalMetaData } from "../types";

/** Inputs for `useDocMetaData` (order id, modal metadata, auth-on-file snapshot). */
interface UseDocMetaDataParams {
  orderId: string;
  metaData: NarLetterModalMetaData | undefined;
  // singleMedicalPaOrder: MedicalPaOrder | null | undefined;
  authOnFileDetails: AuthOnFile | null | undefined;
}

/**
 * Manages J-code document metadata and medicine (drug) selection state for the NAR letter modal.
 *
 * When `metaData.incorrectLetterDocPath` is set (incorrect letter flow), shows only J-codes
 * that reference that letter path, all unchecked. Otherwise loads from `UserStore` by doc type
 * and auto-selects when exactly one medicine exists.
 *
 * @param params.orderId - PA order id used to read per-order J-code doc keys from storage.
 * @param params.metaData - Modal metadata (`authStatus`, `docType`, optional incorrect letter path).
 * @param params.authOnFileDetails - Auth-on-file payload used to resolve J-codes for the incorrect path flow.
 * @returns `docMetaData` rows, `selectedMedicines`, and `handleMedicineSelection`.
 */
export const useDocMetaData = ({
  orderId,
  metaData,
  // singleMedicalPaOrder,
  authOnFileDetails,
}: UseDocMetaDataParams) => {
  const [docMetaData, setDocMetaData] = useState<
    DocumentsStoreedInLocalStorage[]
  >([]);
  const [selectedMedicines, setSelectedMedicines] = useState<string[]>([]);

  useEffect(() => {
    const incorrectPath = metaData?.incorrectLetterDocPath;
    if (incorrectPath && authOnFileDetails) {
      const jCodesWithLetter = getJCodesWithAuthLetterDocPathFromAuthOnFile(
        authOnFileDetails,
        incorrectPath,
      );
      const list: DocumentsStoreedInLocalStorage[] = jCodesWithLetter.map(
        (jCode) => ({
          jcodeIdentifier: jCode.id ?? jCode.j_code ?? "",
          jcodeDescription: jCode.medicine_name ?? jCode.description ?? "",
          docType: metaData?.docType,
          isDocPresent: false,
        }),
      );
      setDocMetaData(list);
      setSelectedMedicines([]);
      return;
    }

    const allDocsGroupedByType = UserStore.getItem(
      `${UserGeneralInfoContextKeys.JCODE_DOCS}_${orderId}`,
    );
    const requiredMedicineNames =
      allDocsGroupedByType?.[metaData?.docType ?? ""];
    if (requiredMedicineNames && requiredMedicineNames.length === 1) {
      setSelectedMedicines([requiredMedicineNames[0]?.jcodeIdentifier ?? ""]);
    }
    setDocMetaData(requiredMedicineNames ?? []);
  }, [
    metaData?.authStatus,
    metaData?.incorrectLetterDocPath,
    metaData?.docType,
    orderId,
    authOnFileDetails,
  ]);

  const handleMedicineSelection = (
    medicineName: string,
    isChecked: boolean,
  ) => {
    setSelectedMedicines((prev) => {
      const newMedicines = [...prev];
      if (isChecked) {
        newMedicines.push(medicineName);
      } else {
        const idx = newMedicines.indexOf(medicineName);
        if (idx !== -1) newMedicines.splice(idx, 1);
      }
      return newMedicines;
    });
  };

  return {
    docMetaData,
    selectedMedicines,
    handleMedicineSelection,
  };
};
